import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Banner, Button, Dialog, List, ListItem, Skeleton, TextField } from '@meshble/molo';
import { api, must, type Instance } from '../lib/api';
import { quandoRelativo } from '../lib/attivita';
import { usePermessiSu } from '../session';
import { ConPermesso } from '../permesso';
import { Anello } from '../brand';
import { Login } from '../icons';
import { t } from '../lib/lingua';

/**
 * «Entra in Odoo»: si sceglie un utente e si entra nell'istanza come lui,
 * senza conoscerne la password. È il modo di vedere quello che vede un
 * cliente quando dice «non funziona».
 *
 * La sessione la apre l'hub sul server; qui arriva un link che vale un
 * minuto e una volta sola. La scheda si apre *prima* di chiedere il link,
 * vuota: un `window.open` dopo una risposta di rete lo bloccano i browser,
 * uno dentro il clic no.
 */
export function EntraInOdoo({ slug, istanza }: { slug: string; istanza: Instance }) {
  const permessi = usePermessiSu(slug, { istanza: istanza.id });
  const [apri, setApri] = useState(false);
  if (istanza.status !== 'running') return null;

  return (
    <>
      <ConPermesso ha={permessi} serve="instance.login">
        <Button variant="tonal" icon={<Login />} onClick={() => setApri(true)}>{t('Entra in Odoo')}</Button>
      </ConPermesso>
      <Dialog
        open={apri}
        onOpenChange={setApri}
        title={t('Entra in Odoo')}
        description={t('Come chi vuoi entrare in «{nome}»? Si apre in una scheda nuova, già dentro. Per Odoo è un login normale di quell’utente; nel registro di Molo resta scritto che sei stato tu.', { nome: istanza.name })}
        actions={<Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>}
      >
        {apri && <Utenti slug={slug} istanza={istanza} chiudi={() => setApri(false)} />}
      </Dialog>
    </>
  );
}

function Utenti({ slug, istanza, chiudi }: { slug: string; istanza: Instance; chiudi: () => void }) {
  const q = useQuery({
    queryKey: ['odoo-users', istanza.id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/instances/{id}/users', { params: { path: { slug, id: istanza.id } } })),
  });
  const [scelto, setScelto] = useState<number | null>(null);
  const [cerca, setCerca] = useState('');

  const entra = useMutation({
    mutationFn: async ({ uid, scheda }: { uid: number; scheda: Window | null }) => {
      try {
        const { url } = await must(api.POST('/api/v1/orgs/{slug}/instances/{id}/login', {
          params: { path: { slug, id: istanza.id } },
          body: { uid },
        }));
        if (scheda) scheda.location.href = url;
        else window.open(url, '_blank');
      } catch (e) {
        // La scheda vuota aperta al clic non deve restare lì a non dire niente.
        scheda?.close();
        throw e;
      }
    },
    onSuccess: chiudi,
    onSettled: () => setScelto(null),
  });

  if (q.error) return <Banner tone="error" title={t('Non riesco a leggere gli utenti')}>{q.error.message}</Banner>;
  if (q.isPending) return <div className="md-stack"><Skeleton /><Skeleton /><Skeleton /></div>;
  const utenti = q.data ?? [];
  if (utenti.length === 0) {
    return <p className="md-body-medium" style={{ margin: 0 }}>{t('Nessun utente interno attivo in questa istanza.')}</p>;
  }
  const trovati = utenti.filter((u) => corrisponde(cerca, u.name, u.login));

  return (
    <div className="md-stack">
      {entra.error && <Banner tone="error" title={t('Non sono riuscito a entrare')}>{(entra.error as Error).message}</Banner>}
      {/* Con pochi utenti si vede tutto a colpo d'occhio; con tanti si cerca
          per nome o per login, e il campo ha già il fuoco. */}
      {utenti.length > 5 && (
        <TextField
          label=""
          aria-label={t('Cerca un utente')}
          placeholder={t('Cerca per nome o login')}
          autoFocus
          value={cerca}
          onChange={(e) => setCerca(e.target.value)}
        />
      )}
      {trovati.length === 0 && (
        <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Nessun utente corrisponde a «{cerca}».', { cerca })}
        </p>
      )}
      <List>
        {trovati.map((u) => (
          <ListItem
            key={u.id}
            interactive
            aria-disabled={entra.isPending}
            headline={u.name || u.login}
            supporting={u.lastLogin
              ? t('{login} · ultimo accesso {quando}', { login: u.login, quando: quandoRelativo(u.lastLogin) })
              : t('{login} · mai entrato', { login: u.login })}
            trailing={scelto === u.id ? <Anello /> : undefined}
            onClick={() => {
              if (entra.isPending) return;
              setScelto(u.id);
              entra.mutate({ uid: u.id, scheda: window.open('', '_blank') });
            }}
          />
        ))}
      </List>
    </div>
  );
}

/** Senza accenti né maiuscole: «jose» trova «José», e ogni parola cercata deve stare nel nome o nel login. */
export function corrisponde(cerca: string, ...campi: string[]): boolean {
  const piatto = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const testo = piatto(campi.join(' '));
  return cerca.trim().split(/\s+/).filter(Boolean).every((parola) => testo.includes(piatto(parola)));
}
